import React from 'react';
import { Trans } from '@lingui/react/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { useSelector } from 'react-redux';
import { DocumentType } from '@/types';
import { IState } from '@/state/IState';
import { groupBy, sortBy } from 'lodash-es';
import { Select } from '@ygtechnologies/bygr-frontend-component-library';

type DocumentTypeSelectProps = {
    required?: boolean;
    value: string | number;
    filter?(type: DocumentType): boolean;
    fieldName?: string;
    placeholder?: string;
    onChange(selectedId: number | null, selectedType: DocumentType | null): void;
};

export const DocumentTypeSelectGrouped: React.FC<DocumentTypeSelectProps> = ({
    filter = (type) => true,
    onChange,
    value,
    placeholder,
    fieldName,
    required = false,
}) => {
    const itemCategories = useSelector((state: IState) => state.global.itemCategories);
    const documentTypes = useSelector((state: IState) => state.seller.documentTypes);

    if (!documentTypes) {
        const loadingOption = { label: <Trans>Loading...</Trans>, value: 0 };
        return <Select options={[loadingOption]} value={loadingOption} />;
    }

    function convertTypes(documentTypes: DocumentType[]) {
        return sortBy(documentTypes, ['sortOrder', 'id']).map((type) => ({
            label: type.name,
            value: type.id,
            source: type,
        }));
    }

    const filteredTypes = documentTypes!.filter(filter);
    const typesByCategory = groupBy(filteredTypes, 'itemCategoryId');
    const groupedTypes = itemCategories
        .filter((cat) => typesByCategory[cat.id] && typesByCategory[cat.id].length > 0)
        .map((cat) => ({
            label: cat.name,
            value: cat.id,
            options: convertTypes(typesByCategory[cat.id]),
        }));

    const uncategorizedTypes = convertTypes(filteredTypes.filter((x) => !x.itemCategoryId));

    const selectedOption = value
        ? groupedTypes
              .flatMap((x) => x.options)
              .concat(uncategorizedTypes)
              .find((x) => x.value === +value)
        : null;

    return (
        <Select
            onChange={(v: any) => triggerHandler(onChange, v.value, v.source)}
            options={uncategorizedTypes.concat(groupedTypes as any)}
            value={selectedOption}
            fieldName={fieldName}
            placeholder={placeholder}
            required={required}
        />
    );
};

export const DocumentTypeSelect: React.FC<DocumentTypeSelectProps> = ({
    filter = (type) => true,
    onChange,
    value,
    placeholder,
    fieldName,
    required = false,
}) => {
    const documentTypes = useSelector((state: IState) => state.seller.documentTypes);

    if (!documentTypes) {
        const loadingOption = { label: <Trans>Loading...</Trans>, value: 0 };
        return (
            <Select
                options={[loadingOption]}
                value={loadingOption}
                fieldName={fieldName}
                placeholder={placeholder}
                required={required}
            />
        );
    }

    const filteredTypes = sortBy(documentTypes!.filter(filter), ['name']).map((type) => ({
        label: type.name,
        value: type.id,
        source: type,
    }));

    const selectedOption = value ? filteredTypes.find((x) => x.value === +value) : null;

    return (
        <Select
            onChange={(v: any) => triggerHandler(onChange, v.value, v.source)}
            options={filteredTypes}
            value={selectedOption}
            fieldName={fieldName}
            placeholder={placeholder}
            required={required}
        />
    );
};
